import type { SetStatusV3Status } from "@redotech/redo-flows-common/rules/actions";
import { SetStatusSnoozeDuration } from "@redotech/redo-flows-common/rules/actions";
import type { SetStatusV3Action as SetStatusV3ActionType } from "@redotech/redo-flows-common/rules/schemas";
import { t, useTranslation } from "@redotech/redo-i18n";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useMemo } from "react";

const statusOptions = (): RedoListItem<SetStatusV3Status>[] => [
  {
    id: "OPEN",
    type: "text",
    text: t("Open", { context: "support ticket status" }),
    value: "OPEN",
  },
  {
    id: "CLOSED",
    type: "text",
    text: t("Closed", { context: "support ticket status" }),
    value: "CLOSED",
  },
  {
    id: "SNOOZED",
    type: "text",
    text: t("Snoozed", { context: "support ticket status" }),
    value: "SNOOZED",
  },
  {
    id: "IN_PROGRESS",
    type: "text",
    text: t("In progress", { context: "support ticket status" }),
    value: "IN_PROGRESS",
  },
  {
    id: "ARCHIVED",
    type: "text",
    text: t("Archived", { context: "support ticket status" }),
    value: "ARCHIVED",
  },
  {
    id: "DELETED",
    type: "text",
    text: t("Deleted", { context: "support ticket status" }),
    value: "DELETED",
  },
];

const snoozeDurationOptions = (): RedoListItem<SetStatusSnoozeDuration>[] => [
  {
    id: SetStatusSnoozeDuration.ONE_HOUR,
    type: "text",
    text: t("1 hour"),
    value: SetStatusSnoozeDuration.ONE_HOUR,
  },
  {
    id: SetStatusSnoozeDuration.FOUR_HOURS,
    type: "text",
    text: t("4 hours"),
    value: SetStatusSnoozeDuration.FOUR_HOURS,
  },
  {
    id: SetStatusSnoozeDuration.TWENTY_FOUR_HOURS,
    type: "text",
    text: t("24 hours"),
    value: SetStatusSnoozeDuration.TWENTY_FOUR_HOURS,
  },
];

export const SetStatusV3Action = memo(function SetStatusV3Action({
  action,
  onChange,
  editableFields,
}: {
  action: SetStatusV3ActionType;
  onChange: (updated: SetStatusV3ActionType) => void;
  /**
   * When omitted, all fields are editable. When provided, only fields
   * in the set are editable.
   */
  editableFields?: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const statusEditable =
    editableFields === undefined || editableFields.has("status");
  const snoozeDurationEditable =
    editableFields === undefined || editableFields.has("snoozeDuration");

  const selectedStatus = useMemo(
    () => statusOptions().find((opt) => opt.value === action.status),
    [action.status],
  );

  const selectedSnoozeDuration = useMemo(
    () =>
      snoozeDurationOptions().find(
        (opt) => opt.value === action.snoozeDuration,
      ),
    [action.snoozeDuration],
  );

  return (
    <Flex dir="column" gap="md" w="full">
      <RedoSingleSelectDropdownInput<SetStatusV3Status>
        disabled={!statusEditable}
        fitToAnchor
        label={t("Status", { context: "support ticket status" })}
        options={statusOptions()}
        optionSelected={({ item }) => {
          if (!statusEditable) {
            return;
          }
          onChange({ ...action, status: item.value });
        }}
        selectedItem={selectedStatus}
      />
      {action.status === "SNOOZED" && (
        <RedoSingleSelectDropdownInput<SetStatusSnoozeDuration>
          disabled={!snoozeDurationEditable}
          fitToAnchor
          label={t("For", {
            context: "how long a snoozed ticket stays snoozed",
          })}
          options={snoozeDurationOptions()}
          optionSelected={({ item }) => {
            if (!snoozeDurationEditable) {
              return;
            }
            onChange({ ...action, snoozeDuration: item.value });
          }}
          selectedItem={selectedSnoozeDuration}
        />
      )}
    </Flex>
  );
});
